import type { ReactNode } from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'

export function ShowMoreClampFade({
  visible,
  className,
  forceMount = false,
}: {
  visible: boolean
  className?: string
  /** Keep in DOM and use opacity (e.g. animated fade-out when expanding). */
  forceMount?: boolean
}) {
  if (!forceMount && !visible) return null
  return (
    <div
      className={cn(
        'from-white/80 dark:from-background/80 pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t to-transparent',
        forceMount
          ? cn(
              'transition-opacity duration-200 ease-out motion-reduce:transition-none',
              visible ? 'opacity-100' : 'opacity-0'
            )
          : 'h-8',
        className
      )}
      aria-hidden
    />
  )
}

export function ShowMoreClampToggle({
  expanded,
  onToggle,
  showMoreLabel = 'Show more',
  showLessLabel = 'Show less',
  className,
}: {
  expanded: boolean
  onToggle: () => void
  showMoreLabel?: string
  showLessLabel?: string
  className?: string
}) {
  return (
    <button
      type='button'
      onClick={onToggle}
      className={cn(
        'mt-2 inline-flex items-center gap-1 text-[12px] font-medium text-cobalt-strong hover:text-[#2D4AB8] dark:text-cobalt-strong dark:hover:text-cobalt-strong',
        className
      )}
    >
      {expanded ? showLessLabel : showMoreLabel}
      <ChevronDown
        className={cn(
          'h-3 w-3 transition-transform',
          expanded && 'rotate-180'
        )}
        aria-hidden
      />
    </button>
  )
}

/** Presentational shell: content + optional fade + optional toggle. */
export function ShowMoreClamp({
  children,
  showFade,
  showToggle,
  expanded,
  onToggle,
  className,
  contentClassName,
  fadeClassName,
  showMoreLabel,
  showLessLabel,
  toggleClassName,
}: {
  children: ReactNode
  showFade: boolean
  showToggle: boolean
  expanded: boolean
  onToggle: () => void
  className?: string
  contentClassName?: string
  fadeClassName?: string
  showMoreLabel?: string
  showLessLabel?: string
  toggleClassName?: string
}) {
  return (
    <div className={className}>
      <div className='relative'>
        <div className={contentClassName}>{children}</div>
        <ShowMoreClampFade visible={showFade} className={fadeClassName} />
      </div>
      {showToggle && (
        <ShowMoreClampToggle
          expanded={expanded}
          onToggle={onToggle}
          showMoreLabel={showMoreLabel}
          showLessLabel={showLessLabel}
          className={toggleClassName}
        />
      )}
    </div>
  )
}
